import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Shield, UserPlus, Loader2 } from "lucide-react";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export default function RegisterPage() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Le mot de passe doit contenir au moins 6 caractères."); return; }
    setLoading(true);
    try {
      await register(email, password, name);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // OAuth Google direct : le backend gere le flux, pose les cookies puis redirige
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[var(--ia-navy)] items-center justify-center p-12">
        <div className="max-w-md text-white animate-fade-in">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-10 w-10 text-[var(--ia-blue)]" />
            <span className="text-3xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>IA Conforme</span>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Rejoignez-nous
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            Créez votre compte et commencez à naviguer en toute sécurité dans la conformité IA.
          </p>
          <div className="mt-8 p-4 bg-white/5 rounded-md border border-white/10">
            <p className="text-sm text-slate-300 mb-2 font-medium">Inclus avec le plan gratuit :</p>
            <ul className="text-sm text-slate-400 space-y-1.5">
              <li>3 messages IA par jour</li>
              <li>Glossaire et timeline complets</li>
              <li>1 diagnostic express</li>
              <li>1 quiz de conformité</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-[var(--ia-surface)]">
        <Card className="w-full max-w-md shadow-sm border-[var(--ia-border)] animate-fade-in">
          <CardHeader className="text-center">
            <div className="flex justify-center lg:hidden mb-4">
              <Shield className="h-8 w-8 text-[var(--ia-navy)]" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Créer un compte</CardTitle>
            <CardDescription>Commencez gratuitement</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full mb-4 h-11" onClick={handleGoogleRegister} data-testid="google-register-btn">
              <GoogleIcon />
              <span className="ml-2">S'inscrire avec Google</span>
            </Button>

            <div className="relative mb-4">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-[var(--ia-muted)]">ou</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200" data-testid="register-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Nom complet</Label>
                <Input data-testid="register-name-input" id="name" placeholder="Jean Dupont" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input data-testid="register-email-input" id="email" type="email" placeholder="votre@email.fr" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input data-testid="register-password-input" id="password" type="password" placeholder="Minimum 6 caractères" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button data-testid="register-submit-button" type="submit" className="w-full bg-[var(--ia-navy)] hover:bg-[var(--ia-navy)]/90" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Créer mon compte
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-[var(--ia-text-muted)]">
              Déjà un compte ?{" "}
              <Link to="/login" className="text-[var(--ia-blue)] hover:underline font-medium" data-testid="go-to-login-link">
                Se connecter
              </Link>
            </div>
            <div className="mt-4 flex items-center justify-center gap-3 text-[10px] text-[var(--ia-muted)]">
              <a href="/legal?section=mentions" className="hover:underline">Mentions légales</a>
              <span>·</span>
              <a href="/legal?section=cgu" className="hover:underline">CGU</a>
              <span>·</span>
              <a href="/legal?section=confidentialite" className="hover:underline">Confidentialité</a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
